import type {
  loan_application,
  StatusGroup,
} from '@axo/shared/data-access/types';
import type { Customer } from '@axo/shared/data-access/types/customer';
import type { Person } from '@axo/shared/data-access/types/person';
import type { IAPI } from '../../lib/models/api';
import { send } from '../../lib/send';

export function getLoanApplication(api: IAPI) {
  return async function getLoanApplication(
    id: string
  ): Promise<loan_application.LoanApplication> {
    return send({
      baseURL: api.url.service,
      path: `/loan-application/${id}`,
      token: api.token,
      method: 'GET',
    });
  };
}

export function getLoanApplicationRevision(api: IAPI) {
  return async function getLoanApplicationRevision({
    applicationID,
    revision,
  }: {
    applicationID: string;
    revision: number;
  }): Promise<loan_application.LoanApplication> {
    return send({
      baseURL: api.url.service,
      path: `/loan-application/${encodeURIComponent(
        applicationID
      )}/revisions/${encodeURIComponent(revision)}`,
      token: api.token,
      method: 'GET',
    });
  };
}

export interface MagicOutput extends loan_application.LoanApplication {
  AcceptTermsConditions: boolean;
  AppliedAmount: number;
  Citizenship: string;
  CreatedAt: string;
  CustomerID: string;
  EmailReminders: boolean;
  HumanReadableID: string;
  ID: string;
  IP: string;
  JWT: string;
  LoanDuration: number;
  MagicToken: string;
  MarketCountry: string;
  SMSReminders: boolean;
  TermsAndConditionsText: string;
  UpdatedAt: string;
  WaitingForBanks: boolean;
}

export const getLoanApplicationFromMagicToken = async (
  baseURL: string,
  magicToken: string | null
): Promise<MagicOutput> =>
  send({
    baseURL,
    path: `/loan-application/magic/${magicToken}`,
    method: 'GET',
  });

export function getLoanApplicationFromLegacyId(api: IAPI) {
  return async function getLoanApplication({
    legacyID,
    customerID,
  }: {
    legacyID: string | null;
    customerID: string | null;
  }): Promise<loan_application.LoanApplication> {
    const loanApplications = (await send({
      baseURL: api.url.service,
      path: `loan-application?legacyID=${legacyID}&customerID=${customerID}`,
      token: api.token,
      method: 'GET',
    })) as loan_application.LoanApplication[];
    return loanApplications[0];
  };
}

export function postLoanApplication({
  url: { service: baseURL },
  token,
}: IAPI) {
  return (
    loanApplication: Partial<loan_application.LoanApplication>
  ): Promise<loan_application.LoanApplication> =>
    send({
      baseURL,
      path: `/loan-application`,
      token,
      method: 'POST',
      body: loanApplication,
    });
}

export function deleteLoanApplicationCoApplicant({
  url: { service: baseURL },
  token,
}: IAPI) {
  return (applicationID?: string): Promise<loan_application.LoanApplication> =>
    send({
      baseURL,
      path: `/loan-application/${applicationID}/co-applicant`,
      token,
      method: 'DELETE',
    });
}

export function postLoanApplicationIntegration({
  url: { integration },
  token,
}: IAPI) {
  return (body: {
    application: Partial<loan_application.LoanApplication>;
    customer: Partial<Customer>;
    person?: Partial<Person>;
  }): Promise<{ jwt: string; applicationID: string }> =>
    send({
      baseURL: integration ?? '',
      path: `v1/loan-application/`,
      token,
      method: 'POST',
      body,
    });
}

export interface LoanApplicationPatchError {
  applicationIDs: string[];
  message: string;
  code: number;
}

export function patchLoanApplication({
  url: { service: baseURL },
  token,
}: IAPI) {
  return async (
    id: string,
    loanApplication: loan_application.UpdateLoanApplication,
    options?: { signal?: AbortSignal }
  ): Promise<loan_application.LoanApplication> =>
    send({
      baseURL,
      path: `${baseURL}/loan-application/${id}`,
      token,
      method: 'PATCH',
      body: loanApplication,
      signal: options?.signal,
    });
}

export function archiveLoanApplication({
  url: { service: baseURL },
  token,
}: IAPI) {
  return (
    id: string,
    archiveRequestType: loan_application.ArchiveRequestType
  ): Promise<loan_application.LoanApplication> =>
    send({
      baseURL,
      path: `/loan-application/${id}/archive`,
      token,
      method: 'POST',
      body: { ArchiveRequestType: archiveRequestType },
    });
}

export type LoanApplicationByCustomerIDParams = {
  customerId: string;
  pagination: { offset: number; limit: number };
  groups: StatusGroup[];
};

export function findLoanApplicationByCustomerID(api: IAPI) {
  return async function findLoanApplicationByCustomerID({
    customerId,
    pagination = { offset: 0, limit: 100 },
    groups = [],
  }: LoanApplicationByCustomerIDParams): Promise<
    loan_application.LoanApplication[]
  > {
    const groupParams = groups
      ? groups.map((group) => `&group=${encodeURIComponent(group)}`).join('')
      : '';

    return send({
      baseURL: api.url.service,
      path: `/loan-application?customerID=${customerId}&pageSize=${
        pagination.limit
      }&pageNumber=${pagination.offset + 1}${groupParams}`,
      token: api.token,
      method: 'GET',
    });
  };
}

export type LoanApplicationByPersonIDParams = {
  personID: string;
  pagination: { offset: number; limit: number };
  groups: StatusGroup[];
};

export function findLoanApplicationByPersonID(api: IAPI) {
  return async function findLoanApplicationByPersonID({
    personID,
    pagination = { offset: 0, limit: 100 },
    groups = [],
  }: LoanApplicationByPersonIDParams): Promise<
    loan_application.LoanApplication[]
  > {
    const groupParams = groups
      ? groups.map((group) => `&group=${encodeURIComponent(group)}`).join('')
      : '';

    return send({
      baseURL: api.url.service,
      path: `/loan-application?personID=${personID}&pageSize=${
        pagination.limit
      }&pageNumber=${pagination.offset + 1}${groupParams}`,
      token: api.token,
      method: 'GET',
    });
  };
}

/**
 * Creates a new loan application based on an existing one by applying specified modifications.
 * @param api - The API configuration object.
 * @returns A promise that resolves to the modified loan application.
 * @throws An error if the integration URL is missing.
 * @deprecated Use `postApplicationReapplyV2`
 * (`@axo/shared/data-access/api` → `loan_quote_presentation/applicationReapplyV2`).
 * This v1 `/v1/loan-application/reapply/:id` endpoint is reached only by the
 * deprecated `useReapplyApplication` hook and has no live callers after MV-2616.
 */
export function reapplyLoanApplication(api: IAPI) {
  return async (
    loanApplicationID: string,
    data?: Partial<loan_application.LoanApplication>
  ): Promise<loan_application.LoanApplication> => {
    if (!api.url.integration)
      throw new Error('Error: Integration URL is missing');

    return send({
      baseURL: api.url.integration,
      token: api.token,
      path: `v1/loan-application/reapply/${loanApplicationID}`,
      method: 'POST',
      body: data,
    });
  };
}
